(() => {
  if (window.FTRFreeDesignBundle) return;

  const sectionStates = new WeakMap();
  const initializedSections = new WeakSet();
  const controlDisabledStates = new WeakMap();

  function getSelectedLoopSellingPlanId() {
    const value =
      typeof window.FTRLoopBundle?.getSelectedSellingPlanId === "function"
        ? window.FTRLoopBundle.getSelectedSellingPlanId()
        : window.FTRLoopBundle?.selectedSellingPlanId;
    if (value == null || value === "") return null;
    const numericId = Number(String(value).split("/").pop());
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  function logFinalCartPayload(items, selectedSellingPlanId) {
    console.log(
      "[FTR Loop Bundle] Selected selling plan before cart:",
      selectedSellingPlanId,
    );
    console.log("[FTR Loop Bundle] Final Loop-style payload:", { items });
  }

  function getSlots(section) {
    return Array.from(section.querySelectorAll("[data-bundle-slot]"));
  }

  function getState(section) {
    if (!sectionStates.has(section)) {
      sectionStates.set(section, {
        slots: new Map(),
        activeSlotId: null,
        isAddingToCart: false,
      });
    }
    return sectionStates.get(section);
  }

  function getSlot(section, slotId) {
    return (
      getSlots(section).find((slot) => slot.dataset.bundleSlotId === slotId) ||
      null
    );
  }

  function getModal(section, slotId) {
    return (
      Array.from(section.querySelectorAll("[data-bundle-modal]")).find(
        (modal) => modal.dataset.bundleSlotId === slotId,
      ) || null
    );
  }

  function getRequiredQuantity(slotOrModal) {
    return Number.parseInt(slotOrModal?.dataset.requiredQuantity || "0", 10);
  }

  function getModalProducts(modal) {
    return Array.from(modal?.querySelectorAll("[data-modal-product]") || []);
  }

  function getProductQuantity(card) {
    return Number.parseInt(
      card.querySelector("[data-product-quantity]")?.value || "0",
      10,
    );
  }

  function getModalSelectedQuantity(modal) {
    return getModalProducts(modal).reduce(
      (total, card) => total + getProductQuantity(card),
      0,
    );
  }

  function updateProductCardUI(card) {
    const quantity = getProductQuantity(card);
    const selected = quantity > 0;
    card.classList.toggle("selected", selected);
    card.classList.toggle("is-selected", selected);
    card.setAttribute("aria-selected", String(selected));

    const plus = card.querySelector("[data-product-plus]");
    const minus = card.querySelector("[data-product-minus]");
    if (plus) plus.setAttribute("aria-pressed", String(selected));
    if (minus) minus.setAttribute("aria-pressed", String(selected));
  }

  function updateModalUI(modal) {
    if (!modal) return;
    const selectedQuantity = getModalSelectedQuantity(modal);
    const requiredQuantity = getRequiredQuantity(modal);
    const limitReached = selectedQuantity >= requiredQuantity;
    const count = modal.querySelector("#popup-count");
    const next = modal.querySelector("[data-modal-next]");

    if (count)
      count.textContent = `${selectedQuantity} out of ${requiredQuantity} selected`;
    if (next) next.disabled = selectedQuantity !== requiredQuantity;

    getModalProducts(modal).forEach((card) => {
      const quantity = getProductQuantity(card);
      const available = card.dataset.productAvailable === "true";
      const plus = card.querySelector("[data-product-plus]");
      const minus = card.querySelector("[data-product-minus]");

      if (plus) {
        plus.disabled = !available || limitReached;
        plus.setAttribute("aria-disabled", String(plus.disabled));
      }
      if (minus) {
        minus.disabled = !available || quantity <= 0;
        minus.setAttribute("aria-disabled", String(minus.disabled));
      }
      updateProductCardUI(card);
    });
  }

  function resetModalTemporaryState(modal) {
    getModalProducts(modal).forEach((card) => {
      const input = card.querySelector("[data-product-quantity]");
      if (input) input.value = "0";
      updateProductCardUI(card);
    });
  }

  function restoreModalSelection(modal, savedSlot) {
    if (!savedSlot) return;
    savedSlot.items.forEach((savedItem) => {
      const card = getModalProducts(modal).find(
        (product) =>
          product.dataset.variantId === String(savedItem.variantId) &&
          product.dataset.productAvailable === "true",
      );
      const input = card?.querySelector("[data-product-quantity]");
      if (input) input.value = String(savedItem.quantity);
    });
  }

  function openSlotModal(section, slotId) {
    const state = getState(section);
    const modal = getModal(section, slotId);
    if (!modal) return;

    state.activeSlotId = slotId;
    resetModalTemporaryState(modal);
    restoreModalSelection(modal, state.slots.get(slotId));
    updateModalUI(modal);
    section.querySelectorAll("[data-bundle-modal]").forEach((candidate) => {
      candidate.classList.toggle("active", candidate === modal);
    });
  }

  function readModalItems(modal) {
    return getModalProducts(modal)
      .filter(
        (card) =>
          card.dataset.productAvailable === "true" &&
          getProductQuantity(card) > 0,
      )
      .map((card) => ({
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        quantity: getProductQuantity(card),
        title:
          card.dataset.productTitle ||
          card.querySelector("p")?.textContent ||
          "",
        image:
          card.dataset.productImage || card.querySelector("img")?.src || "",
      }));
  }

  function renderSlot(section, slotId) {
    const slot = getSlot(section, slotId);
    if (!slot) return;

    const savedSlot = getState(section).slots.get(slotId);
    const renderedItems = [];
    savedSlot?.items.forEach((item) => {
      for (let index = 0; index < item.quantity; index += 1)
        renderedItems.push(item);
    });

    slot.querySelectorAll("[data-bundle-slot-item]").forEach((row, index) => {
      const item = renderedItems[index];
      const image = row.querySelector("img");
      const label = row.querySelector(".design-label");

      if (item) {
        if (image) {
          image.src = item.image;
          image.srcset = item.image;
          image.alt = item.title;
        }
        if (label) label.textContent = item.title;
        row.dataset.productId = item.productId;
        row.dataset.variantId = item.variantId;
        row.classList.add("selected", "is-selected");
        row.setAttribute("aria-selected", "true");
      } else {
        if (image) {
          image.src = row.dataset.defaultImg;
          image.srcset = row.dataset.defaultImg;
          image.alt = slot.dataset.slotTitle || row.dataset.defaultTitle;
        }
        if (label) label.textContent = row.dataset.defaultTitle;
        row.removeAttribute("data-product-id");
        row.removeAttribute("data-variant-id");
        row.classList.remove("selected", "is-selected", "active");
        row.setAttribute("aria-selected", "false");
      }
    });

    const firstItem = savedSlot?.items[0];
    slot.querySelectorAll("[data-selected-variant-id]").forEach((input) => {
      if ("value" in input) input.value = firstItem?.variantId || "";
    });
    slot.querySelectorAll("[data-selected-product-id]").forEach((input) => {
      if ("value" in input) input.value = firstItem?.productId || "";
    });
    slot.querySelectorAll("[data-selected-quantity]").forEach((input) => {
      if ("value" in input) input.value = String(savedSlot?.totalQuantity || 0);
    });
  }

  function isBundleComplete(section) {
    const state = getState(section);
    return getSlots(section).every((slot) => {
      const savedSlot = state.slots.get(slot.dataset.bundleSlotId);
      return savedSlot && savedSlot.totalQuantity === getRequiredQuantity(slot);
    });
  }

  function getAddControls(section) {
    const controls = [];
    const main = section.querySelector(
      '.js-product-form-main .product-info__add-button [type="submit"]',
    );
    const sticky = section
      .closest(".container")
      ?.querySelector(".card--sticky-atc .card__buttons a");
    if (main) controls.push(main);
    if (sticky) controls.push(sticky);
    return controls;
  }

  function updateAddToCartAvailability(section) {
    const state = getState(section);
    const complete = isBundleComplete(section);
    getAddControls(section).forEach((control) => {
      const unavailable = state.isAddingToCart || !complete;
      if ("disabled" in control) {
        const nativeDisabled = control.dataset.bundleNativeDisabled === "true";
        control.disabled = nativeDisabled || unavailable;
      }
      control.setAttribute("aria-disabled", String(unavailable));
    });
  }

  function syncActiveSlotFromModal(section, modal) {
    const state = getState(section);
    const slotId = state.activeSlotId || modal.dataset.bundleSlotId;
    if (!slotId) return;

    const items = readModalItems(modal);
    const totalQuantity = items.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    if (items.length === 0) state.slots.delete(slotId);
    else state.slots.set(slotId, { slotId, items, totalQuantity });

    renderSlot(section, slotId);
    syncLegacySelectedItems(section);
    updateAddToCartAvailability(section);
  }

  function navigateFromActiveSlot(section, modal) {
    const state = getState(section);
    const slotId = state.activeSlotId || modal.dataset.bundleSlotId;
    const slots = getSlots(section);
    const currentIndex = slots.findIndex(
      (slot) => slot.dataset.bundleSlotId === slotId,
    );
    const nextSlot = currentIndex >= 0 ? slots[currentIndex + 1] : null;

    modal.classList.remove("active");
    state.activeSlotId = null;
    if (nextSlot) openSlotModal(section, nextSlot.dataset.bundleSlotId);
  }

  function handleProductQuantity(section, button, difference) {
    const modal = button.closest("[data-bundle-modal]");
    const card = button.closest("[data-modal-product]");
    const input = card?.querySelector("[data-product-quantity]");
    if (!modal || !card || !input || card.dataset.productAvailable !== "true")
      return;

    const currentQuantity = getProductQuantity(card);
    const selectedQuantity = getModalSelectedQuantity(modal);
    if (difference > 0 && selectedQuantity >= getRequiredQuantity(modal))
      return;

    input.value = String(Math.max(0, currentQuantity + difference));
    updateProductCardUI(card);
    syncActiveSlotFromModal(section, modal);
    updateModalUI(modal);
  }

  function syncLegacySelectedItems(section) {
    window.selectedItems = (window.selectedItems || []).filter(
      (item) => item.freeDesignSection !== section,
    );
    getState(section).slots.forEach((slot) => {
      slot.items.forEach((item) => {
        window.selectedItems.push({
          ...item,
          popupId: slot.slotId,
          freeDesignSection: section,
        });
      });
    });
  }

  function buildContentsList(items) {
    return items.map((item) => `${item.quantity}× : ${item.title}`).join(" | ");
  }

  function buildCartItems(section) {
    const selectedItems = [];
    getState(section).slots.forEach((slot) =>
      selectedItems.push(...slot.items),
    );
    const uniqueItems = new Map();

    selectedItems.forEach((item) => {
      const variantId = Number(item.variantId);
      const quantity = Number(item.quantity || 0);
      if (!Number.isInteger(variantId) || variantId <= 0 || quantity <= 0)
        return;
      const key = String(variantId);
      const existing = uniqueItems.get(key);
      if (existing) existing.quantity += quantity;
      else uniqueItems.set(key, { id: variantId, quantity });
    });

    return Array.from(uniqueItems.values());
  }

  function ensureLoadingMarkup(control) {
    if (!control || control.querySelector(".bundle-add-button__loader")) return;
    const text = document.createElement("span");
    text.className = "bundle-add-button__text";
    while (control.firstChild) text.appendChild(control.firstChild);
    const loader = document.createElement("span");
    loader.className = "bundle-add-button__loader";
    loader.hidden = true;
    loader.setAttribute("aria-hidden", "true");
    control.append(text, loader);
  }

  function setControlLoading(control, loading) {
    ensureLoadingMarkup(control);
    if (loading) controlDisabledStates.set(control, Boolean(control.disabled));
    control.classList.toggle("is-bundle-loading", loading);
    control.setAttribute("aria-busy", String(loading));
    if ("disabled" in control)
      control.disabled = loading || Boolean(controlDisabledStates.get(control));
    control.querySelector(".bundle-add-button__text").hidden = loading;
    control.querySelector(".bundle-add-button__loader").hidden = !loading;
    if (!loading) controlDisabledStates.delete(control);
  }

  function showError(section, message) {
    let error = section.querySelector(".js-form-error");
    if (!error) {
      error = document.createElement("div");
      section.appendChild(error);
    }
    error.classList.add("bundle-add-error");
    error.setAttribute("role", "alert");
    error.setAttribute("aria-live", "polite");
    error.textContent = message;
    error.hidden = false;
  }

  function clearError(section) {
    const error = section.querySelector(".bundle-add-error");
    if (error) {
      error.textContent = "";
      error.hidden = true;
    }
  }

  function resetBundleSelection(section) {
    const state = getState(section);
    state.slots.clear();
    state.activeSlotId = null;
    getSlots(section).forEach((slot) =>
      renderSlot(section, slot.dataset.bundleSlotId),
    );
    section.querySelectorAll("[data-bundle-modal]").forEach((modal) => {
      modal.classList.remove("active");
      resetModalTemporaryState(modal);
      updateModalUI(modal);
    });
    syncLegacySelectedItems(section);
  }

  async function handleFinalAddToCart(event, section, trigger) {
    const state = getState(section);
    event.preventDefault();
    event.stopPropagation();
    if (state.isAddingToCart) return;

    if (!isBundleComplete(section)) {
      const missingSlot = getSlots(section).find((slot) => {
        const saved = state.slots.get(slot.dataset.bundleSlotId);
        return !saved || saved.totalQuantity !== getRequiredQuantity(slot);
      });
      if (missingSlot) openSlotModal(section, missingSlot.dataset.bundleSlotId);
      return;
    }

    let items = buildCartItems(section);
    if (items.length === 0) {
      showError(section, "Unable to prepare the completed bundle.");
      return;
    }

    state.isAddingToCart = true;
    clearError(section);
    const controls = getAddControls(section);
    controls.forEach((control) => setControlLoading(control, true));
    let succeeded = false;

    try {
      window.FTRLoopBundle?.syncSelectedPlanFromWidget?.(section);
      const isLoopSubscription =
        window.FTRLoopBundle?.purchaseType === "subscription";
      if (!window.FTRLoopBundle?.prepareCartItems) {
        throw new Error(
          "Loop bundle setup is not ready. Please refresh and try again.",
        );
      }
      // Both one-time and subscription bundles use Loop transactions.
      // Never send a synthetic parent line / Cart Transform payload to Shopify.
      items = await window.FTRLoopBundle.prepareCartItems(items, section);
      const selectedSellingPlanId = getSelectedLoopSellingPlanId();
      if (isLoopSubscription && !selectedSellingPlanId) {
        throw new Error("Please select a valid subscription frequency.");
      }
      if (!Array.isArray(items) || items.length === 0) {
        console.error(
          "[FTR Loop Bundle] Empty cart items before Shopify request.",
          { items },
        );
        throw new Error("The bundle cart payload is empty.");
      }
      logFinalCartPayload(items, selectedSellingPlanId);
      const shopifyCartPayload = { items };
      const pendingAttributes =
        window.FTRLoopBundle?.pendingBundleCartAttributes;
      if (pendingAttributes && typeof pendingAttributes === "object") {
        shopifyCartPayload.attributes = pendingAttributes;
      }
      if (window.FTRLoopBundle) {
        window.FTRLoopBundle.pendingBundleCartAttributes = null;
      }
      console.log(
        "[FTR Loop Bundle] Stage 6 Shopify payload:",
        shopifyCartPayload,
      );
      console.log(
        "[FTR Loop Bundle] Final /cart/add.js payload:",
        shopifyCartPayload,
      );
      console.log(
        "[FTR Loop Bundle] Final Shopify add request:",
        shopifyCartPayload,
      );

      const response = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart/add.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(shopifyCartPayload),
        },
      );
      const cartRawText = await response.text();
      let result;
      try {
        result = cartRawText ? JSON.parse(cartRawText) : null;
      } catch (error) {
        console.error("[FTR Loop Bundle] Invalid Shopify cart JSON:", {
          cartRawText,
          error,
        });
        throw new Error("Shopify returned an invalid cart response.");
      }
      console.log("[FTR Loop Bundle] Shopify cart response:", {
        status: response.status,
        payload: result,
      });
      if (!response.ok) {
        if (selectedSellingPlanId) {
          console.error(
            "[FTR Loop Bundle] Shopify rejected the Loop-style subscription payload.",
            {
              sellingPlanShopifyId: selectedSellingPlanId,
              items,
              response: result,
            },
          );
          throw new Error(
            "The selected subscription option is unavailable for one or more bundle items.",
          );
        }
        throw new Error(
          result.description ||
            result.message ||
            "Unable to add products to cart.",
        );
      }
      if (
        isLoopSubscription &&
        window.FTRLoopBundle?.validateAddedBundleLines
      ) {
        await window.FTRLoopBundle.validateAddedBundleLines(items);
      }
      resetBundleSelection(section);
      succeeded = true;

      if (
        succeeded &&
        window.FTRCart &&
        typeof window.FTRCart.open === "function"
      ) {
        await window.FTRCart.open();
      } else {
        document.dispatchEvent(
          new CustomEvent("dispatch:cart-drawer:open", { bubbles: true }),
        );
      }
    } catch (error) {
      console.error("Bundle add-to-cart error:", error);
      showError(
        section,
        error.customerMessage ||
          error.message ||
          "Something went wrong while adding to cart.",
      );
    } finally {
      state.isAddingToCart = false;
      controls.forEach((control) => setControlLoading(control, false));
      updateAddToCartAvailability(section);
    }
  }

  function handleSectionClick(event) {
    const section = event.currentTarget;
    const plus = event.target.closest("[data-product-plus]");
    const minus = event.target.closest("[data-product-minus]");
    const productTile = event.target.closest("[data-modal-product]");
    const preventTileClick = event.target.closest("[data-prevent-tile-click]");
    const edit = event.target.closest("[data-edit-bundle-slot]");
    const next = event.target.closest("[data-modal-next]");
    const close = event.target.closest(".popup-close");
    const add = event.target.closest(
      '.js-product-form-main .product-info__add-button [type="submit"]',
    );

    if (minus) {
      event.preventDefault();
      event.stopPropagation();
      handleProductQuantity(section, minus, -1);
    } else if (preventTileClick) {
      event.stopPropagation();
    } else if (plus) {
      event.preventDefault();
      event.stopPropagation();
      handleProductQuantity(section, plus, 1);
    } else if (productTile && !preventTileClick) {
      event.preventDefault();
      event.stopPropagation();
      const tilePlus = productTile.querySelector("[data-product-plus]");
      if (tilePlus && !tilePlus.disabled)
        handleProductQuantity(section, tilePlus, 1);
    } else if (next) {
      event.preventDefault();
      event.stopPropagation();
      navigateFromActiveSlot(section, next.closest("[data-bundle-modal]"));
    } else if (close) {
      event.preventDefault();
      event.stopPropagation();
      close.closest("[data-bundle-modal]")?.classList.remove("active");
      getState(section).activeSlotId = null;
    } else if (edit) {
      event.preventDefault();
      event.stopPropagation();
      openSlotModal(section, edit.dataset.slotId);
    } else if (add) {
      handleFinalAddToCart(event, section, add);
    } else if (event.target.matches("[data-bundle-modal]")) {
      event.target.classList.remove("active");
      getState(section).activeSlotId = null;
    }
  }

  function handleSectionKeydown(event) {
    const edit = event.target.closest("[data-edit-bundle-slot]");
    if (edit && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      openSlotModal(event.currentTarget, edit.dataset.slotId);
    }
  }

  function initializeFreeDesignSection(section) {
    if (!section) return;
    if (initializedSections.has(section)) {
      section.querySelectorAll("[data-bundle-modal]").forEach(updateModalUI);
      updateAddToCartAvailability(section);
      return;
    }
    initializedSections.add(section);
    section.dataset.freeDesignInitialized = "true";
    getState(section);
    section.addEventListener("click", handleSectionClick);
    section.addEventListener("keydown", handleSectionKeydown);
    section.addEventListener(
      "submit",
      (event) => {
        if (event.target.matches(".js-product-form-main")) {
          handleFinalAddToCart(
            event,
            section,
            event.target.querySelector('[type="submit"]') || event.target,
          );
        }
      },
      true,
    );

    getAddControls(section).forEach((control) => {
      control.dataset.bundleNativeDisabled = String(Boolean(control.disabled));
    });
    section.querySelectorAll("[data-bundle-modal]").forEach(updateModalUI);
    updateAddToCartAvailability(section);
  }

  function initializeBundles(root = document) {
    const sections = new Set();
    if (root.matches?.("[data-bundle-slot]")) {
      const section = root.closest(".product-info");
      if (section) sections.add(section);
    }
    root.querySelectorAll?.("[data-bundle-slot]").forEach((slot) => {
      const section = slot.closest(".product-info");
      if (section) sections.add(section);
    });
    sections.forEach(initializeFreeDesignSection);
  }

  function initFreeGiftPopup(popupId) {
    const modal = Array.from(
      document.querySelectorAll(`[id="${popupId}"]`),
    ).find((candidate) => candidate.matches("[data-bundle-modal]"));
    const section = modal?.closest(".product-info");
    if (section && modal) {
      initializeFreeDesignSection(section);
      openSlotModal(section, modal.dataset.bundleSlotId);
    }
  }

  document.addEventListener("click", (event) => {
    const sticky = event.target.closest(".card--sticky-atc .card__buttons a");
    const section = sticky
      ?.closest(".container")
      ?.querySelector(".product-info");
    if (sticky && section?.querySelector("[data-bundle-slot]")) {
      handleFinalAddToCart(event, section, sticky);
    }
  });
  document.addEventListener("shopify:section:load", (event) =>
    initializeBundles(event.target),
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeBundles());
  } else {
    initializeBundles();
  }

  window.initFreeGiftPopup = initFreeGiftPopup;
  window.FTRFreeDesignBundle = {
    initialize: initializeBundles,
    reset: resetBundleSelection,
  };
})();
